import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = 'https://api.glowytics.com';

function getAdminHeaders() {
  return {
    'Content-Type': 'application/json',
    'x-admin-token': localStorage.getItem('adminToken') || '',
  };
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
      <div style={{
        display: 'inline-block',
        width: 32,
        height: 32,
        border: '3px solid #e5e7eb',
        borderTop: '3px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
function EmptyState({ message }) {
  return (
    <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af', fontSize: 14 }}>
      {message || 'Veri bulunamadı.'}
    </div>
  );
}

// ─── Overview Card ────────────────────────────────────────────────────────────
function OverviewCard({ label, value, color }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      padding: '20px 24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      flex: 1,
      minWidth: 160,
    }}>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || '#111827' }}>{value ?? '—'}</div>
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  const bg = type === 'error' ? '#fee2e2' : '#d1fae5';
  const color = type === 'error' ? '#991b1b' : '#065f46';
  return (
    <div style={{
      position: 'fixed', bottom: 28, right: 28, zIndex: 10000,
      background: bg, color, borderRadius: 10, padding: '12px 20px',
      fontSize: 13, fontWeight: 500, boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
      display: 'flex', alignItems: 'center', gap: 12, maxWidth: 360,
    }}>
      {message}
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color, fontWeight: 700, fontSize: 15 }}>✕</button>
    </div>
  );
}

// ─── Collapsible ──────────────────────────────────────────────────────────────
function Collapsible({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 12, fontWeight: 500, padding: 0 }}
      >
        {open ? '▲' : '▶'} {label}
      </button>
      {open && <div style={{ marginTop: 6 }}>{children}</div>}
    </div>
  );
}

// ─── Sparkline (inline SVG, no external libs) ─────────────────────────────────
function Sparkline({ history }) {
  if (!history || history.length === 0) {
    return <span style={{ fontSize: 11, color: '#d1d5db' }}>—</span>;
  }
  // history comes newest-first; reverse to chronological order
  const chrono = [...history].reverse();
  const values = chrono.map(h => Number(h.disapproved) || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const width = 120;
  const height = 28;
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const last = values[values.length - 1];
  const prevMax = values.length > 1 ? Math.max(...values.slice(0, -1)) : last;
  const rising = last > prevMax;
  const strokeColor = rising ? '#dc2626' : '#6366f1';
  const lastX = (values.length - 1) * stepX;
  const lastY = height - ((last - min) / range) * height;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <polyline
        points={points}
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={lastX} cy={lastY} r="2.5" fill={strokeColor} />
    </svg>
  );
}

// ─── Severity / alertType badges ───────────────────────────────────────────────
function AlertTypeBadge({ alertType, severity }) {
  if (!alertType) return null;
  const labelMap = { spike: 'Ani Artış', ratio: 'Yüksek Oran' };
  const sevColors = {
    critical: { bg: '#fee2e2', color: '#991b1b' },
    warning: { bg: '#fef9c3', color: '#92400e' },
  };
  const sc = sevColors[severity] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{
      background: sc.bg, color: sc.color, borderRadius: 99,
      padding: '2px 10px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {labelMap[alertType] || alertType}{severity ? ` · ${severity}` : ''}
    </span>
  );
}

function pctColor(disapprovedPct, triggered) {
  if (triggered || disapprovedPct >= 0.5) return '#dc2626';
  if (disapprovedPct >= 0.3) return '#d97706';
  return '#16a34a';
}

// ─── Settings (config) inline form ─────────────────────────────────────────────
function BrandSettingsForm({ brandId, onClose, onSaved, showToast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    enabled: true,
    minCatalogSize: '',
    spikePct: '',
    spikeAbs: '',
    ratioThreshold: '',
    alertCooldownH: '',
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE}/api/admin/mc-disapproval-monitor/config/${brandId}`, {
      headers: getAdminHeaders(), credentials: 'include',
    })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        const c = data.config || {};
        setForm({
          enabled: c.enabled ?? true,
          minCatalogSize: c.minCatalogSize ?? '',
          spikePct: c.spikePct != null ? (Number(c.spikePct) * 100).toString() : '',
          spikeAbs: c.spikeAbs ?? '',
          ratioThreshold: c.ratioThreshold != null ? (Number(c.ratioThreshold) * 100).toString() : '',
          alertCooldownH: c.alertCooldownH ?? '',
        });
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [brandId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        enabled: form.enabled,
        minCatalogSize: form.minCatalogSize !== '' ? Number(form.minCatalogSize) : undefined,
        spikePct: form.spikePct !== '' ? Number(form.spikePct) / 100 : undefined,
        spikeAbs: form.spikeAbs !== '' ? Number(form.spikeAbs) : undefined,
        ratioThreshold: form.ratioThreshold !== '' ? Number(form.ratioThreshold) / 100 : undefined,
        alertCooldownH: form.alertCooldownH !== '' ? Number(form.alertCooldownH) : undefined,
      };
      const res = await fetch(`${API_BASE}/api/admin/mc-disapproval-monitor/config/${brandId}`, {
        method: 'PUT', headers: getAdminHeaders(), credentials: 'include', body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Kayıt başarısız');
      showToast && showToast('Ayarlar kaydedildi.');
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      showToast && showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 12 }}><Spinner /></div>;

  const fieldStyle = {
    width: '100%', border: '1px solid #e5e7eb', borderRadius: 6,
    padding: '6px 10px', fontSize: 13, boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 11, color: '#6b7280', fontWeight: 500, marginBottom: 3, display: 'block' };

  return (
    <div style={{
      marginTop: 12, background: '#f9fafb', border: '1px solid #e5e7eb',
      borderRadius: 10, padding: 16,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Etkin</label>
          <select
            value={form.enabled ? 'true' : 'false'}
            onChange={e => setForm(f => ({ ...f, enabled: e.target.value === 'true' }))}
            style={fieldStyle}
          >
            <option value="true">Evet</option>
            <option value="false">Hayır</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Min. Katalog Boyutu</label>
          <input type="number" value={form.minCatalogSize}
            onChange={e => setForm(f => ({ ...f, minCatalogSize: e.target.value }))} style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>Ani Artış Eşiği (%)</label>
          <input type="number" value={form.spikePct}
            onChange={e => setForm(f => ({ ...f, spikePct: e.target.value }))} style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>Ani Artış Eşiği (adet)</label>
          <input type="number" value={form.spikeAbs}
            onChange={e => setForm(f => ({ ...f, spikeAbs: e.target.value }))} style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>Oran Eşiği (%)</label>
          <input type="number" value={form.ratioThreshold}
            onChange={e => setForm(f => ({ ...f, ratioThreshold: e.target.value }))} style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>Alarm Bekleme (saat)</label>
          <input type="number" value={form.alertCooldownH}
            onChange={e => setForm(f => ({ ...f, alertCooldownH: e.target.value }))} style={fieldStyle} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer', color: '#374151' }}>
          İptal
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: '#6366f1', color: 'white', border: 'none', borderRadius: 6,
            padding: '6px 14px', fontSize: 13, fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </div>
    </div>
  );
}

// ─── Brand Row Card ─────────────────────────────────────────────────────────────
function BrandRow({ row, onDismissAlert, onConfigSaved, showToast }) {
  const [showSettings, setShowSettings] = useState(false);
  const {
    brandId, brandName, merchantId, hasData, fetchDate,
    active, disapproved, total, disapprovedPct, baselineDisapproved,
    triggered, alertType, severity, reasons, topIssues, history, activeAlert,
  } = row;

  const color = hasData ? pctColor(disapprovedPct || 0, triggered) : '#9ca3af';

  const handleDismiss = async () => {
    if (!activeAlert?.id) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/mc-disapproval-monitor/alerts/${activeAlert.id}/dismiss`, {
        method: 'POST', headers: getAdminHeaders(), credentials: 'include',
      });
      if (!res.ok) throw new Error((await res.json()).message || 'İşlem başarısız');
      showToast && showToast('Alarm kapatıldı.');
      onDismissAlert && onDismissAlert();
    } catch (err) {
      showToast && showToast(err.message, 'error');
    }
  };

  return (
    <div style={{
      background: triggered ? '#fef2f2' : '#f9fafb',
      border: `1px solid ${triggered ? '#fecaca' : '#e5e7eb'}`,
      borderRadius: 12, padding: '18px 22px', marginBottom: 14,
    }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Brand info */}
        <div style={{ minWidth: 200, flex: '1 1 200px' }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{brandName || brandId}</div>
          <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>
            {merchantId ? `Merchant ID: ${merchantId}` : 'Merchant ID yok'}
          </div>
          {fetchDate && (
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
              Veri tarihi: {fetchDate}
            </div>
          )}
        </div>

        {/* Disapproved figure */}
        <div style={{ minWidth: 140, textAlign: 'center' }}>
          {hasData ? (
            <>
              <div style={{ fontSize: 20, fontWeight: 700, color }}>
                {(disapprovedPct * 100).toFixed(0)}%
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {disapproved} / {total} reddedildi
              </div>
              {baselineDisapproved != null && (
                <div style={{ fontSize: 11, color: '#9ca3af' }}>önceki taban: {baselineDisapproved}</div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 12, color: '#9ca3af' }}>veri yok</div>
          )}
        </div>

        {/* Active count */}
        <div style={{ minWidth: 90, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>Aktif Ürün</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#374151' }}>{active ?? '—'}</div>
        </div>

        {/* Sparkline */}
        <div style={{ minWidth: 130 }}>
          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>Trend</div>
          <Sparkline history={history} />
        </div>

        {/* Badges */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', minWidth: 140 }}>
          <AlertTypeBadge alertType={alertType} severity={severity} />
          {activeAlert && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{
                background: '#dc2626', color: 'white', borderRadius: 99,
                padding: '2px 10px', fontSize: 11, fontWeight: 700,
              }}>
                Aktif Alarm
              </span>
              <button
                onClick={handleDismiss}
                style={{
                  background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb',
                  borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 500,
                }}
              >
                Kapat
              </button>
            </div>
          )}
          <button
            onClick={() => setShowSettings(s => !s)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 12, fontWeight: 500, padding: 0 }}
          >
            {showSettings ? 'Ayarlar ▲' : 'Ayarlar ▼'}
          </button>
        </div>
      </div>

      {/* Reasons + top issues */}
      {(reasons?.length > 0 || topIssues?.length > 0) && (
        <div style={{ marginTop: 12 }}>
          <Collapsible label="Detaylar">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
              {reasons?.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Nedenler</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {reasons.map((r, i) => (
                      <div key={i} style={{ fontSize: 12, color: '#4b5563' }}>
                        <span style={{ fontWeight: 500, color: '#6b7280' }}>[{r.type}]</span> {r.detail}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {topIssues?.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>En Sık Görülen Sorunlar</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {topIssues.map((issue, i) => (
                      <div key={i} style={{ fontSize: 12, color: '#4b5563', display: 'flex', gap: 8 }}>
                        <span style={{ flex: 1 }}>{issue.description || issue.code}</span>
                        <span style={{ fontWeight: 600, color: '#dc2626' }}>{issue.numItems}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Collapsible>
        </div>
      )}

      {showSettings && (
        <BrandSettingsForm
          brandId={brandId}
          onClose={() => setShowSettings(false)}
          onSaved={onConfigSaved}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminMcDisapprovalMonitor() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => setToast({ message, type });

  const fetchOverview = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/admin/mc-disapproval-monitor/overview`, {
      headers: getAdminHeaders(), credentials: 'include',
    })
      .then(r => r.json())
      .then(data => {
        setOverview(data);
        setLoading(false);
        setError(null);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/mc-disapproval-monitor/run-now`, {
        method: 'POST', headers: getAdminHeaders(), credentials: 'include',
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Çalıştırma başarısız');
      showToast('Değerlendirme tetiklendi.');
      setTimeout(() => {
        fetchOverview();
        setRunning(false);
      }, 2000);
    } catch (err) {
      showToast(err.message, 'error');
      setRunning(false);
    }
  };

  const summary = overview?.summary || {};
  const rows = overview?.rows || [];

  return (
    <div style={{ padding: '24px', maxWidth: 1400, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>
            Merchant Center — Disapproval Monitor
          </h1>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
            Markalar arası Merchant Center ürün reddi izleme paneli
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={handleRunNow}
            disabled={running}
            style={{
              background: '#059669', color: 'white', border: 'none', borderRadius: 6,
              padding: '7px 16px', fontSize: 13, fontWeight: 500,
              cursor: running ? 'not-allowed' : 'pointer', opacity: running ? 0.7 : 1,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            {running && (
              <span style={{
                width: 12, height: 12, border: '2px solid rgba(255,255,255,0.4)', borderTop: '2px solid white',
                borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite',
              }} />
            )}
            {running ? 'Çalıştırılıyor...' : 'Şimdi Çalıştır'}
          </button>
          <button
            onClick={fetchOverview}
            style={{ background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
          >
            Yenile
          </button>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13 }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontWeight: 700 }}>✕</button>
        </div>
      )}

      {/* DRY_RUN Banner */}
      {summary.dryRun && (
        <div style={{
          background: '#fef3c7', color: '#92400e', borderRadius: 8, padding: '10px 16px',
          marginBottom: 16, fontSize: 13, fontWeight: 500, border: '1px solid #fcd34d',
        }}>
          DRY_RUN açık — alarm koşulları değerlendiriliyor ama mail atılmıyor.
        </div>
      )}

      {/* Summary Tiles */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        {loading ? (
          [1, 2, 3, 4].map(k => (
            <div key={k} style={{ background: 'white', borderRadius: 12, padding: '20px 24px', flex: 1, minWidth: 160, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ background: '#f3f4f6', borderRadius: 4, height: 14, width: '60%', marginBottom: 10 }} />
              <div style={{ background: '#f3f4f6', borderRadius: 4, height: 28, width: '40%' }} />
            </div>
          ))
        ) : (
          <>
            <OverviewCard label="Toplam Marka" value={summary.totalBrands} color="#6366f1" />
            <OverviewCard label="Veri Var" value={summary.withData} color="#0891b2" />
            <OverviewCard label="Tetiklenen" value={summary.triggered} color={summary.triggered > 0 ? '#dc2626' : '#059669'} />
            <OverviewCard label="Aktif Alarm" value={summary.activeAlerts} color={summary.activeAlerts > 0 ? '#dc2626' : '#059669'} />
          </>
        )}
      </div>

      {/* Brand rows */}
      <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <EmptyState message="Marka verisi bulunamadı." />
        ) : (
          <div>
            {rows.map(row => (
              <BrandRow
                key={row.brandId}
                row={row}
                onDismissAlert={fetchOverview}
                onConfigSaved={fetchOverview}
                showToast={showToast}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
